import {
  Badge,
  Button,
  buttonVariants,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Popover,
  PopoverContent,
  Separator,
  SuspenseLoader,
  toast,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@folio/components';
import { BranchNameEditor } from '@folio/components/module-elements/BranchContext/BranchNameEditor';
import { Card, CardContent, CardHeader, CardTitle } from '@folio/components/ui/card';
import { formatDateWithTime } from '@folio/lib/date-formatting';
import { getBranchDisplayName } from '@folio/utils/branchDisplayName';
import { isStopRequestedByCustomer } from '@folio/utils/stop-branch';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useAccessiblePayerCustomerIds } from '@folio/hooks';
import { buildRulePayers } from '@folio/utils/buildRulePayers';
import {
  branchControllerForkBranchWithoutPolicy,
  branchControllerInvalidateBranch,
  BranchDto,
  BranchType,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  CommentsControllerListCommentsQueryResponse,
  commentsControllerListCommentsQueryOptions,
  DerogationAction,
  KeyValueFieldObject,
  legacyPermissionsControllerAtMeQueryOptions,
  ListBranchesOutput,
  PolicyDto,
  renewalControllerFinalizeRenewalBranch,
  useDerogationControllerApproveDerogation,
  useDerogationControllerDenyDerogation,
  customerControllerGetCustomerQueryOptions,
  CustomerDto,
  keyValueFieldsToObject,
  viewSpecificControllerListPolicyComplianceAlertsQueryOptions,
  customerControllerGetCustomerPaymentMethodsQueryOptions,
  PaymentMethodDtoWithIsPreferred,
  policyControllerGetPolicyQueryKey,
  policyControllerGetPolicyQueryOptions,
  policyControllerListPolicyDocumentsQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  GetCustomerPaymentMethodsOutput,
  Customer,
} from 'korint-sdk';
import {
  ArchiveRestoreIcon,
  CheckCheckIcon,
  CheckIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
  GitCompareArrows,
  InfoIcon,
  PencilIcon,
  ArchiveIcon,
  XIcon,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AssetCompareModule } from '../AssetCompareModule/AssetCompareModule';
import { ClaimsReviewModule } from '../ClaimsReviewModule/ClaimsReviewModule';
import { CustomerCompareModule } from '../CustomerCompareModule/CustomerCompareModule';
import { DocumentsUploadPolicyModule } from '../DocumentsUploadPolicyModule/DocumentsUploadPolicyModule';
import { StopCancellationSummary } from '../../funnels/Stop/StopCancellationSummary';
import {
  invalidateDerogationViews,
  waitForDeniedBranchClose,
} from '../../utils/derogation-decision';
import { StopRequestSummary } from '../../funnels/Stop/StopRequestSummary';
import { StopLineActions } from './StopLineActions';
import { InvoicesScheduleCompareModule } from '../InvoicesScheduleCompareModule/InvoicesScheduleCompareModule';
import { PolicyCompareModule } from '../PolicyCompareModule';
import { PremiumCompareModule } from '../PremiumCompareModule/PremiumCompareModule';
import { QuoteTierCompareModule } from '../QuoteTierCompareModule/QuoteTierCompareModule';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { CreateDerogationButton } from '../FinalizeNewBusinessModule/LegacyModule/CreateDerogationButton';
import { SendContractButton } from '../FinalizeNewBusinessModule/LegacyModule/SendContractButton';
import { useFeatureFlaggedRules } from '../FinalizeNewBusinessModule/useFeatureFlaggedRules';
import { FinalizeNewBusinessActions } from '../FinalizeNewBusinessModule/FinalizeNewBusinessActions';
import { useFinalizeNewBusinessButtonsFlag } from '../FinalizeNewBusinessModule/useFinalizeNewBusinessButtonsFlag';
import { useKorintConfig } from 'korint-sdk/react';
import { PopoverAnchor } from '@radix-ui/react-popover';
import { type BranchSection, partitionBranches } from './partitionBranches';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { branchFunnelPath } from '@folio/utils/branchFunnelPath';

const CARD_BRANCH_TYPES = ['STOP', 'MTA', 'RENEWAL', 'REPRICING', 'NEW_BUSINESS'] as const;

function CompareButton({
  branch,
  policyOnBranch,
  refetchPolicyOnBranch,
  refetchPolicyOnDashboard,
  isWholesale,
}: {
  branch: BranchDto;
  policyOnBranch: Omit<PolicyDto, 'customFields'> & { customFields: KeyValueFieldObject };
  refetchPolicyOnBranch: () => void;
  refetchPolicyOnDashboard: () => void;
  isWholesale: boolean;
}) {
  const { t } = useTranslation();
  const BASE_BRANCH = undefined;
  const { policyOnMain } = usePolicyOnDashboard();
  const policyDashboardEventsModuleConfig = usePolicyProductConfig(
    'policyDashboardEventsModuleConfig',
  );
  const productCustomPath = usePolicyProductConfig('productCustomPath');

  const [comment, setComment] = useState<string | undefined>(undefined);
  const [isDerogationDialogOpen, setIsDerogationDialogOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const lastDerogation = useMemo(() => {
    return branch.derogations
      ?.slice()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())?.[0];
  }, [branch.derogations]);

  const derogationFunnelUrl = useMemo(() => {
    if (
      lastDerogation?.action === 'QUOTE' &&
      policyDashboardEventsModuleConfig.quoteDerogationFunnelModule
    ) {
      return `/quote-derogation${productCustomPath}/${policyOnBranch.customers[0]}/${policyOnBranch.id}/${branch.id}/${policyDashboardEventsModuleConfig.quoteDerogationFunnelModule.steps[0].path}`;
    }

    return undefined;
  }, [
    lastDerogation?.action,
    policyOnBranch.customers[0],
    policyOnBranch.id,
    branch.id,
    productCustomPath,
  ]);

  const { mutateAsync: approveDerogation, isPending: isApproveDerogationPending } =
    useDerogationControllerApproveDerogation({
      mutation: {
        onSuccess: async () => {
          toast({
            variant: 'default',
            title: t('policyDashboardEvents.toast.approveSuccess'),
            description: t('policyDashboardEvents.toast.approveSuccessDescription'),
          });
          if (derogationFunnelUrl) {
            navigate(derogationFunnelUrl);
          }
          setIsDerogationDialogOpen(false);
          refetchPolicyOnBranch();
          refetchPolicyOnDashboard();
          await invalidateDerogationViews(queryClient);
          // The derogation list reads a search index reindexed shortly after the decision.
          setTimeout(() => void invalidateDerogationViews(queryClient), 2000);
        },
      },
    });

  const { mutateAsync: denyDerogation, isPending: isDenyDerogationPending } =
    useDerogationControllerDenyDerogation({
      mutation: {
        onSuccess: async () => {
          setIsDerogationDialogOpen(false);
          await invalidateDerogationViews(queryClient);
          if (branch.branchType === 'NEW_BUSINESS') {
            navigate(`/derogations`);
          } else {
            navigate(`/policies/${policyOnBranch.id}/events`);
            refetchPolicyOnBranch();
            // Not awaited: the fork runs in background jobs and can take seconds to land.
            void waitForDeniedBranchClose(queryClient, {
              policyId: policyOnBranch.id,
              branchId: branch.id,
            })
              .catch(() => false)
              .then(() =>
                Promise.all([
                  queryClient.invalidateQueries({
                    queryKey: policyControllerGetPolicyQueryKey(policyOnBranch.id),
                  }),
                  invalidateDerogationViews(queryClient),
                ]),
              );
          }
          await refetchPolicyOnDashboard();
        },
      },
    });

  const { data: derogationComment } = useQuery({
    ...commentsControllerListCommentsQueryOptions(branch.id, { branchId: branch.id }),
    //For now, we only allow one comment per entity/branch couple
    select: (data: CommentsControllerListCommentsQueryResponse | undefined) =>
      data?.data?.[0]?.comment,
  });
  useEffect(() => setComment(derogationComment), [derogationComment]);

  const isStopBranch = branch.branchType === 'STOP';

  const {
    isDerogationPending,
    isDerogationWithdrawn,
    isDerogationApproved,
    isDerogationDenied,
    isDerogation,
    branchesToCompare,
  } = useMemo(() => {
    const isBranchOpen = branch.branchStatus === 'BRANCH_OPEN';
    // A cancelled stop closes its branch; its derogation stays CREATED with nothing left to decide.
    const isDerogationPending = lastDerogation?.status === 'CREATED' && isBranchOpen;
    const isDerogationWithdrawn = lastDerogation?.status === 'CREATED' && !isBranchOpen;
    const isDerogationApproved = lastDerogation?.status === 'APPROVED';
    const isDerogationDenied = lastDerogation?.status === 'DENIED';

    const isQuoteDerogationInProgress =
      lastDerogation?.action === 'QUOTE' && policyOnBranch.status === 'POLICY_CREATED';

    const isSignDerogationInProgress =
      lastDerogation?.action === 'SIGN' && !Boolean(branch.mergedAt);

    const isConfirmDerogationInProgress =
      lastDerogation?.action === 'CONFIRM' && !Boolean(branch.mergedAt);

    const isStopDerogationInProgress = lastDerogation?.action === 'STOP' && isBranchOpen;

    const isDerogation =
      isQuoteDerogationInProgress ||
      isSignDerogationInProgress ||
      isConfirmDerogationInProgress ||
      isStopDerogationInProgress ||
      isDerogationDenied;

    const branchesToCompare =
      branch.branchType === 'NEW_BUSINESS' ? [branch.id] : [BASE_BRANCH, branch.id];

    return {
      isDerogationPending,
      isDerogationWithdrawn,
      isDerogationApproved,
      isDerogationDenied,
      isDerogation,
      branchesToCompare,
    };
  }, [lastDerogation, policyOnBranch.status, branch]);

  // BASE echoes only the live request: a decided branch reading it would describe a later stop
  // while the proofs below still load from this branch. No echo of its own means nothing to show.
  const stopRequest =
    branch.branchStatus === 'BRANCH_OPEN'
      ? (policyOnMain.stop ?? policyOnBranch.stop)
      : policyOnBranch.stop;

  const { data: requiredStopDocuments = [] } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policyOnBranch.productId),
    select: ({ policyStopReasons }) =>
      policyStopReasons?.find(({ reason }) => reason === stopRequest?.reason)?.requiredDocuments ??
      [],
    enabled: isStopBranch,
  });

  return (
    <Dialog open={isDerogationDialogOpen} onOpenChange={setIsDerogationDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" leftIcon={<GitCompareArrows className="h-4 w-4" />}>
          {isWholesale && isDerogationPending
            ? t('policyDashboardEvents.awaitingValidation')
            : isDerogationApproved || isDerogationDenied || isDerogationWithdrawn
              ? t('policyDashboardEvents.viewDecision')
              : isDerogation
                ? t('policyDashboardEvents.derogation')
                : isStopBranch
                  ? t('policyDashboardEvents.stopDetails')
                  : t('policyDashboardEvents.compare')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isDerogation
              ? t('policyDashboardEvents.derogation')
              : isStopBranch
                ? t('policyDashboardEvents.stopDetails')
                : t('policyDashboardEvents.compare')}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {isDerogation
            ? t('policyDashboardEvents.derogationVerification', {
                action: t(
                  `policyDashboardEvents.derogationAction.${lastDerogation?.action as DerogationAction}`,
                ),
              })
            : isStopBranch
              ? t('policyDashboardEvents.stopDetailsDescription')
              : t('policyDashboardEvents.compareDescription')}
        </DialogDescription>
        <SuspenseLoader>
          {/* A stop changes no price nor cover: the request and its proofs are what is reviewed. */}
          {isStopBranch ? (
            <>
              {stopRequest && (
                <StopRequestSummary
                  reason={stopRequest.reason}
                  effectiveAt={stopRequest.stopDate}
                  requestedBy={stopRequest.requestedBy}
                />
              )}
              {branch.branchStatus === 'BRANCH_CLOSED' &&
                (branch.closeReason === 'BROKER_REQUEST' ||
                  branch.closeReason === 'CUSTOMER_REQUEST') &&
                branch.closeComment && <StopCancellationSummary branch={branch} />}
              <DocumentsUploadPolicyModule
                displayContext="broker"
                branchId={branch.id}
                readOnly
                hideAcceptAllButton
                documentsLists={requiredStopDocuments.map(category => ({
                  category,
                  required: true,
                }))}
              />
            </>
          ) : (
            <>
              {policyOnBranch.status !== 'POLICY_CREATED' && (
                <QuoteTierCompareModule branches={branchesToCompare} />
              )}
              {policyOnBranch.status !== 'POLICY_CREATED' && (
                <PremiumCompareModule branches={branchesToCompare} />
              )}
              <CustomerCompareModule branches={branchesToCompare} />
              {policyDashboardEventsModuleConfig.enablePolicyComparison && (
                <PolicyCompareModule branches={branchesToCompare} />
              )}
              {policyDashboardEventsModuleConfig.enableAssetComparison && (
                <AssetCompareModule branches={branchesToCompare} />
              )}
              {policyDashboardEventsModuleConfig.claimsReview && (
                <ClaimsReviewModule
                  config={policyDashboardEventsModuleConfig.claimsReview}
                  branches={branchesToCompare}
                  policies={branchesToCompare.map(branchId =>
                    branchId === BASE_BRANCH ? policyOnMain : policyOnBranch,
                  )}
                />
              )}
              {policyOnBranch.status !== 'POLICY_CREATED' && (
                <InvoicesScheduleCompareModule branches={branchesToCompare} />
              )}
            </>
          )}
        </SuspenseLoader>

        {((isDerogationPending && isWholesale) || isDerogationDenied) && (
          <Card>
            <CardHeader>
              <CardTitle>{t('policyDashboardEvents.denialComment')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                placeholder={t('policyDashboardEvents.yourComment')}
                value={comment}
                onChange={e => setComment(e.target.value)}
                disabled={isDerogationDenied}
              />
            </CardContent>
          </Card>
        )}

        <DialogFooter hidden={(!isDerogationPending && !isDerogationApproved) || !isWholesale}>
          {isDerogationPending && (
            <Group align="stretch" justify="end" gap={2}>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<CheckCheckIcon className="h-4 w-4" />}
                onClick={async () => {
                  if (comment) {
                    toast({
                      variant: 'warning',
                      title: t('policyDashboardEvents.toast.approveWithCommentWarning'),
                    });

                    return;
                  }

                  await approveDerogation({
                    branchId: branch.id,
                    derogationId: lastDerogation!.derogationId,
                  });
                }}
                loading={isApproveDerogationPending || isDenyDerogationPending}
              >
                {t('policyDashboardEvents.approve')}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                leftIcon={<XIcon className="h-4 w-4" />}
                // A refusal without a reason reaches the requester as an empty motif.
                disabled={!comment?.trim()}
                onClick={async () =>
                  await denyDerogation({
                    branchId: branch.id,
                    derogationId: lastDerogation!.derogationId,
                    data: { comment: comment!.trim() },
                  })
                }
                loading={isDenyDerogationPending || isApproveDerogationPending}
              >
                {t('policyDashboardEvents.deny')}
              </Button>
            </Group>
          )}

          {isDerogationApproved && Boolean(derogationFunnelUrl) && (
            <Link
              to={derogationFunnelUrl!}
              className={buttonVariants({
                variant: 'ghost',
                size: 'sm',
                class: 'justify-end',
              })}
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              {t('policyDashboardEvents.resumeDerogation')}
            </Link>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useRenewalApprovalStatus(branch: BranchDto, productId: string) {
  const { data: tacitRenewal } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId),
    select: data => {
      if (data.renewal?.__type === 'ConfigRenewalEnabled') {
        return data.renewal.tacitRenewal ?? false;
      }
      return false;
    },
  });

  const isRenewalApproved = useMemo(() => {
    if (branch.renewalStatus === 'RENEWAL_REFUSED') {
      return false;
    }
    if (branch.renewalStatus === 'RENEWAL_ACCEPTED') {
      return true;
    }
    if (!branch.renewalStatus && tacitRenewal) {
      return true;
    }
    return false;
  }, [tacitRenewal, branch]);

  return { isRenewalApproved, tacitRenewal };
}

function RenewalStatusBadge({
  branch,
  policyOnBranch,
}: {
  branch: BranchDto;
  policyOnBranch: Omit<PolicyDto, 'customFields'> & { customFields: KeyValueFieldObject };
}) {
  const { t } = useTranslation();
  const { isRenewalApproved } = useRenewalApprovalStatus(branch, policyOnBranch.productId);

  const dateDisplay = policyOnBranch.endedAt
    ? formatDateWithTime(new Date(policyOnBranch.endedAt))
    : t('policyDashboardEvents.dateUnknown');

  if (policyOnBranch.status !== 'POLICY_QUOTED') {
    return <Badge variant="destructive">{t('policyDashboardEvents.renewal.editProject')}</Badge>;
  }

  if (!isRenewalApproved) {
    return (
      <Badge variant="destructive">
        {t('policyDashboardEvents.renewal.policyStopped', { date: dateDisplay })}
      </Badge>
    );
  }

  return (
    <Badge variant="default">
      {t('policyDashboardEvents.renewal.renewalEffective', { date: dateDisplay })}
    </Badge>
  );
}

function OtherRenewalActionButtons({
  branch,
  policyOnBranch,
  refetchPolicyOnDashboard,
}: {
  branch: BranchDto;
  policyOnBranch: Omit<PolicyDto, 'customFields'> & { customFields: KeyValueFieldObject };
  refetchPolicyOnDashboard: () => void;
}) {
  const { t } = useTranslation();
  const { isRenewalApproved } = useRenewalApprovalStatus(branch, policyOnBranch.productId);

  const { mutateAsync: finalizeRenewalBranch, isPending: isLoading } = useMutation({
    mutationFn: async (finalizationType: 'REFUSED' | 'ACCEPTED') => {
      await renewalControllerFinalizeRenewalBranch(policyOnBranch.id, branch.id, {
        finalizationType,
      });
    },
    onSuccess: () => {
      refetchPolicyOnDashboard();
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: t('policyDashboardEvents.toast.renewalError'),
      });
    },
  });

  if (!isRenewalApproved) {
    return (
      <Group justify="start" gap={2} className="w-full">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => finalizeRenewalBranch('ACCEPTED')}
          loading={isLoading}
          className="w-full justify-start"
          leftIcon={<CheckIcon className="h-4 w-4" />}
        >
          {t('policyDashboardEvents.renewal.accept')}
        </Button>
        <Tooltip>
          <TooltipTrigger>
            <InfoIcon className="h-4 w-4" />
          </TooltipTrigger>
          <TooltipContent>
            {t('policyDashboardEvents.renewal.acceptTooltip', {
              date: policyOnBranch.endedAt
                ? formatDateWithTime(new Date(policyOnBranch.endedAt))
                : t('policyDashboardEvents.dateUnknown'),
            })}
          </TooltipContent>
        </Tooltip>
      </Group>
    );
  }

  return (
    <Group justify="start" gap={2} className="w-full">
      <Button
        variant="destructive-ghost"
        size="sm"
        onClick={() => finalizeRenewalBranch('REFUSED')}
        loading={isLoading}
        className="w-full justify-start"
        leftIcon={<XIcon className="h-4 w-4" />}
      >
        {t('policyDashboardEvents.renewal.refuse')}
      </Button>
      <Tooltip>
        <TooltipTrigger>
          <InfoIcon className="h-4 w-4" />
        </TooltipTrigger>
        <TooltipContent>
          {t('policyDashboardEvents.renewal.refuseTooltip', {
            date: policyOnBranch.endedAt
              ? formatDateWithTime(new Date(policyOnBranch.endedAt))
              : t('policyDashboardEvents.dateUnknown'),
          })}
        </TooltipContent>
      </Tooltip>
    </Group>
  );
}

function OtherActionsPopover({
  branch,
  policyOnBranch,
  refetchPolicyOnDashboard,
}: {
  branch: BranchDto;
  policyOnBranch: Omit<PolicyDto, 'customFields'> & { customFields: KeyValueFieldObject };
  refetchPolicyOnDashboard: () => void;
}) {
  const { t } = useTranslation();
  const productCustomPath = usePolicyProductConfig('productCustomPath');

  const {
    korintConfig: { productsConfig },
  } = useKorintConfig();

  const { renewalFunnelSteps } = productsConfig?.[policyOnBranch.productId] ?? {};

  const [openPopover, setOpenPopover] = useState(false);

  const { data: renewalDocument } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policyOnBranch.id, {
      branchId: branch.id,
      onlyOnBranch: true,
      archived: false,
    }),
    select: data =>
      data.data
        .map(d => ({
          ...d,
          metadata: keyValueFieldsToObject(d.metadata ?? []),
        }))
        .find(doc => doc.category === 'RENEWAL_DOCUMENT'),
  });

  const { mutate: invalidateBranch, isPending: isInvalidateBranchLoading } = useMutation({
    mutationFn: async () => {
      await branchControllerInvalidateBranch(policyOnBranch.id, branch.id, {
        reason: 'BROKER_REQUEST',
      });
    },
    onSuccess: () => {
      toast({
        variant: 'warning',
        title: t('policyDashboardEvents.toast.mtaCancelled'),
      });

      refetchPolicyOnDashboard();
    },
  });

  const hideViewButton = renewalDocument?.signedUrl === undefined;
  const funnelPath = branchFunnelPath({
    branchType: branch.branchType,
    productCustomPath,
    customerId: policyOnBranch.customers[0],
    policyId: policyOnBranch.id,
    branchId: branch.id,
  });
  const hideEditButton =
    isInvalidateBranchLoading ||
    !funnelPath ||
    (branch.branchType === 'RENEWAL' && !renewalFunnelSteps);
  const hideDeleteButton = branch.branchType !== 'MTA';
  const hideRenewalActions = branch.branchType !== 'RENEWAL';

  if (hideViewButton && hideEditButton && hideDeleteButton && hideRenewalActions) {
    return null;
  }

  return (
    <Popover open={openPopover} onOpenChange={setOpenPopover}>
      <PopoverAnchor>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setOpenPopover(true)}
          aria-label={t('policyDashboardEvents.actions.showBranchActions')}
        >
          <EllipsisVerticalIcon aria-hidden="true" />
        </Button>
      </PopoverAnchor>
      <PopoverContent>
        <Stack align="start" gap={2}>
          <BranchNameEditor branch={branch} trigger="text" />
          <a
            href={renewalDocument?.signedUrl}
            target="_blank"
            rel="noreferrer noopener"
            hidden={hideViewButton}
          >
            <Button variant="ghost" hidden={hideViewButton}>
              {t('policyDashboardEvents.actions.view')}
            </Button>
          </a>

          <Link
            to={funnelPath ?? ''}
            className={buttonVariants({
              variant: 'ghost',
              size: 'sm',
              class: 'w-full justify-start',
            })}
            hidden={hideEditButton}
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            {t('policyDashboardEvents.actions.edit')}
          </Link>

          {!hideRenewalActions && (
            <OtherRenewalActionButtons
              branch={branch}
              policyOnBranch={policyOnBranch}
              refetchPolicyOnDashboard={refetchPolicyOnDashboard}
            />
          )}

          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArchiveIcon className="h-4 w-4" />}
            onClick={() => invalidateBranch()}
            className="w-full justify-start"
            loading={isInvalidateBranchLoading}
            hidden={hideDeleteButton}
          >
            {t('policyDashboardEvents.actions.archive')}
          </Button>
        </Stack>
      </PopoverContent>
    </Popover>
  );
}

function EventsLineActions({
  branch,
  policyOnBranch,
  refetchPolicyOnBranch,
  refetchPolicyOnDashboard,
  isWholesale,
  stopFunnelBasePath,
}: {
  branch: BranchDto;
  policyOnBranch: Omit<PolicyDto, 'customFields'> & { customFields: KeyValueFieldObject };
  refetchPolicyOnBranch: () => void;
  refetchPolicyOnDashboard: () => void;
  isWholesale: boolean;
  stopFunnelBasePath?: string;
}) {
  const finalizeNewBusinessModuleConfig = usePolicyProductConfig('finalizeNewBusinessModuleConfig');
  const rules = useFeatureFlaggedRules(finalizeNewBusinessModuleConfig.rules ?? []);

  const customers = useQueries({
    queries: policyOnBranch.customers.map(customerId => ({
      ...customerControllerGetCustomerQueryOptions(customerId),
      select: (data: CustomerDto) => ({
        ...data,
        customFields: keyValueFieldsToObject(data.customFields ?? []),
      }),
    })),
  });

  const paymentMethods = useQueries({
    queries: policyOnBranch.customers.map(customerId => ({
      ...customerControllerGetCustomerPaymentMethodsQueryOptions(customerId, {
        branchId: branch.id,
      }),
      select: (data: GetCustomerPaymentMethodsOutput) => data.data,
    })),
  });

  const { data: hasUnacknowledgedAlerts } = useQuery({
    ...viewSpecificControllerListPolicyComplianceAlertsQueryOptions(policyOnBranch.id),
    select: data => data.data.some(({ acknowledged }) => !acknowledged),
  });

  const payerCustomerIds = useAccessiblePayerCustomerIds(policyOnBranch);

  const payers = buildRulePayers({
    customerIds: policyOnBranch.customers,
    payerCustomerIds,
    customers,
    paymentMethods,
  });

  const isNewFinalizeNewBusinessEnabled = useFinalizeNewBusinessButtonsFlag(
    policyOnBranch.productId,
  );

  return (
    <Group gap={2}>
      <CompareButton
        branch={branch}
        policyOnBranch={policyOnBranch}
        refetchPolicyOnBranch={refetchPolicyOnBranch}
        refetchPolicyOnDashboard={refetchPolicyOnDashboard}
        isWholesale={isWholesale}
      />

      {isNewFinalizeNewBusinessEnabled === true &&
        (branch.branchType === 'MTA' || branch.branchType === 'NEW_BUSINESS') && (
          <FinalizeNewBusinessActions overridenBranchId={branch.id} />
        )}

      {isNewFinalizeNewBusinessEnabled === false &&
        customers[0].data &&
        finalizeNewBusinessModuleConfig.buttons &&
        Boolean(branch.branchType === 'MTA' || branch.branchType === 'NEW_BUSINESS') && (
          <SendContractButton
            rules={rules}
            customer={customers[0].data}
            customers={customers.map(customer => customer.data).filter(Boolean) as Customer[]}
            payers={payers}
            paymentMethods={
              paymentMethods
                .flatMap(paymentMethod => paymentMethod.data)
                .filter(Boolean) as PaymentMethodDtoWithIsPreferred[]
            }
            hasUnacknowledgedAlerts={Boolean(hasUnacknowledgedAlerts)}
            overriddenBranchId={branch.id}
          />
        )}

      {isNewFinalizeNewBusinessEnabled === false &&
        customers[0].data &&
        branch.branchType === 'MTA' &&
        (['QUOTE', 'CONFIRM'] as const).map(action => (
          <CreateDerogationButton
            key={action}
            action={action}
            rules={rules}
            customer={customers[0].data!}
            customers={customers.map(customer => customer.data).filter(Boolean) as Customer[]}
            payers={payers}
            paymentMethods={
              paymentMethods
                .flatMap(paymentMethod => paymentMethod.data)
                .filter(Boolean) as PaymentMethodDtoWithIsPreferred[]
            }
            hasUnacknowledgedAlerts={Boolean(hasUnacknowledgedAlerts)}
            overriddenBranchId={branch.id}
          />
        ))}

      {branch.branchType === 'RENEWAL' && (
        <RenewalStatusBadge branch={branch} policyOnBranch={policyOnBranch} />
      )}

      {branch.branchType === 'STOP' && (
        <StopLineActions branch={branch} stopFunnelBasePath={stopFunnelBasePath} />
      )}

      <OtherActionsPopover
        branch={branch}
        policyOnBranch={policyOnBranch}
        refetchPolicyOnDashboard={refetchPolicyOnDashboard}
      />
    </Group>
  );
}

function EventsLineItem({
  policyId,
  branch,
  isWholesale,
  refetchPolicyOnDashboard,
  stopFunnelBasePath,
}: {
  policyId: string;
  branch: BranchDto;
  isWholesale: boolean;
  refetchPolicyOnDashboard: () => void;
  stopFunnelBasePath?: string;
}) {
  const { t } = useTranslation();
  const { branchId } = useParams();
  const isCurrent = branch.id === branchId;
  const { data: policyOnBranch, refetch: refetchPolicyOnBranch } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId: branch.id }),
    select: (data: PolicyDto) => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const isStopBranch = branch.branchType === 'STOP';
  const isOpen = branch.branchStatus === 'BRANCH_OPEN';
  // A stop is reviewed from its request, whatever became of it: an applied one has no other recap.
  const showDecidedComparison = isStopBranch
    ? !isOpen
    : branch.branchStatus === 'BRANCH_CLOSED' &&
      (branch.derogations?.some(
        derogation => derogation.status === 'DENIED' || derogation.status === 'CREATED',
      ) ??
        false);

  const stopReason = isStopBranch ? policyOnBranch.stop?.reason : undefined;
  const displayName =
    stopReason && !branch.name?.trim()
      ? t('policyDashboardEvents.stopLine.label', {
          reason: t([
            `${policyOnBranch.productId}.stopReasons.${stopReason}`,
            `stopReasons.${stopReason}`,
            stopReason,
          ]),
        })
      : getBranchDisplayName(branch, t);

  return (
    <div
      aria-current={isCurrent ? 'true' : undefined}
      className={isCurrent ? 'rounded-md bg-primary/5 p-2 -mx-2 ring-1 ring-primary/30' : undefined}
    >
      <Group wrap justify="between" gap={2}>
        <Group align="baseline" wrap gap={2} className="min-w-0">
          <div className="break-words">{displayName}</div>
          {isStopBranch && isStopRequestedByCustomer(policyOnBranch.stop?.requestedBy) && (
            <Badge variant="secondary">
              {t('policyDashboardEvents.stopLine.requestedByCustomer')}
            </Badge>
          )}
          {isCurrent && <Badge variant="outline">{t('policyDashboardEvents.youAreHere')}</Badge>}
        </Group>
        {isOpen && (
          <EventsLineActions
            branch={branch}
            policyOnBranch={policyOnBranch}
            refetchPolicyOnBranch={refetchPolicyOnBranch}
            refetchPolicyOnDashboard={refetchPolicyOnDashboard}
            isWholesale={isWholesale}
            stopFunnelBasePath={stopFunnelBasePath}
          />
        )}
        {showDecidedComparison && (
          <CompareButton
            branch={branch}
            policyOnBranch={policyOnBranch}
            refetchPolicyOnBranch={refetchPolicyOnBranch}
            refetchPolicyOnDashboard={refetchPolicyOnDashboard}
            isWholesale={isWholesale}
          />
        )}
        {branch.branchStatus === 'BRANCH_CLOSED' && branch.closeReason === 'RENEWAL_REFUSED' && (
          <Badge variant="destructive">
            {t('policyDashboardEvents.renewal.policyStopped', {
              date: policyOnBranch.endedAt
                ? formatDateWithTime(new Date(policyOnBranch.endedAt))
                : t('policyDashboardEvents.dateUnknown'),
            })}
          </Badge>
        )}
      </Group>
      <Text size="sm" className="text-gray-600 mt-[-2px]">
        {branch.mergedAt
          ? t('policyDashboardEvents.appliedAt', {
              date: formatDateWithTime(new Date(branch.mergedAt)),
            })
          : t('policyDashboardEvents.createdAt', {
              date: formatDateWithTime(new Date(branch.createdAt)),
            })}
      </Text>
    </div>
  );
}

function UnarchiveButton({
  branch,
  alreadyRestored,
  refetchPolicyOnDashboard,
}: {
  branch: BranchDto;
  alreadyRestored: boolean;
  refetchPolicyOnDashboard: () => void;
}) {
  const { t } = useTranslation();
  const [showSuccess, setShowSuccess] = useState(false);

  const { mutate: unarchive, isPending } = useMutation({
    mutationFn: async () => {
      return branchControllerForkBranchWithoutPolicy(branch.id, {
        name: branch.name,
        closeOriginalBranch: false,
      });
    },
    onSuccess: () => {
      setShowSuccess(true);
      refetchPolicyOnDashboard();
      setTimeout(() => setShowSuccess(false), 2000);
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: t('archive.unarchiveError'),
      });
    },
  });

  if (showSuccess || alreadyRestored) {
    return (
      <Button variant="ghost" size="sm" disabled leftIcon={<CheckIcon className="h-4 w-4" />}>
        {t('archive.unarchived')}
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => unarchive()}
      loading={isPending}
      disabled={isPending}
      leftIcon={<ArchiveRestoreIcon className="h-4 w-4" />}
    >
      {t('archive.unarchiveButton')}
    </Button>
  );
}

function ArchivedEventsLineItem({
  branch,
  alreadyRestored,
  refetchPolicyOnDashboard,
}: {
  branch: BranchDto;
  alreadyRestored: boolean;
  refetchPolicyOnDashboard: () => void;
}) {
  const { t } = useTranslation();
  const { branchId } = useParams();
  const isCurrent = branch.id === branchId;

  return (
    <div
      aria-current={isCurrent ? 'true' : undefined}
      className={isCurrent ? 'rounded-md bg-primary/5 p-2 -mx-2 ring-1 ring-primary/30' : undefined}
    >
      <Group wrap justify="between" gap={2}>
        <Group align="baseline" wrap gap={2} className="min-w-0">
          <div className="break-words">{getBranchDisplayName(branch, t)}</div>
          {isCurrent && <Badge variant="outline">{t('policyDashboardEvents.youAreHere')}</Badge>}
        </Group>
        <UnarchiveButton
          branch={branch}
          alreadyRestored={alreadyRestored}
          refetchPolicyOnDashboard={refetchPolicyOnDashboard}
        />
      </Group>
      <Text size="sm" className="text-gray-600 mt-[-2px]">
        {t('archive.createdAt', { date: formatDateWithTime(new Date(branch.createdAt)) })}
      </Text>
    </div>
  );
}

function EventsCardModule({
  branchType,
  isWholesale,
  policyId,
  branches,
  refetchPolicyOnDashboard,
  stopFunnelBasePath,
}: {
  branchType: BranchType;
  isWholesale: boolean;
  policyId: string;
  branches: ListBranchesOutput;
  refetchPolicyOnDashboard: () => void;
  stopFunnelBasePath?: string;
}) {
  const { t } = useTranslation();
  const { branchId } = useParams();

  const groups = useMemo(
    () => partitionBranches(branches?.data ?? [], branchType),
    [branches, branchType],
  );

  const restoredBranchIds = useMemo(
    () =>
      new Set(
        branches?.data
          .filter(branch => branch.branchStatus === 'BRANCH_OPEN' && branch.parentBranchId)
          .map(branch => branch.parentBranchId),
      ),
    [branches],
  );

  if (groups.length === 0) {
    return null;
  }

  const lineItem = (branch: BranchDto) => (
    <EventsLineItem
      key={branch.id}
      branch={branch}
      isWholesale={isWholesale}
      policyId={policyId}
      refetchPolicyOnDashboard={refetchPolicyOnDashboard}
      stopFunnelBasePath={stopFunnelBasePath}
    />
  );

  // Résiliation sections need feminine labels the shared, masculine ones cannot carry.
  const sectionLabel = (section: BranchSection) =>
    t(
      branchType === 'STOP'
        ? [`policyDashboardEvents.stopStatus.${section}`, `policyDashboardEvents.status.${section}`]
        : `policyDashboardEvents.status.${section}`,
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t(`policyDashboardEvents.branchType.${branchType as Exclude<BranchType, 'BASE'>}.title`)}
        </CardTitle>
      </CardHeader>
      <Stack asChild gap={6}>
        <CardContent>
          {groups.map(({ section, branches: sectionBranches }, index) => (
            <Fragment key={section}>
              {index > 0 && <Separator />}
              {section === 'archived' ? (
                <Collapsible
                  key={`${branchId ?? 'base'}:${sectionBranches.some(({ id }) => id === branchId)}`}
                  defaultOpen={sectionBranches.some(({ id }) => id === branchId)}
                >
                  <CollapsibleTrigger className="flex items-center gap-2 font-semibold w-full cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-muted transition-colors [&[data-state=open]>svg]:rotate-90">
                    <ChevronRightIcon className="h-4 w-4 transition-transform duration-200" />
                    {t('archive.archivedSection')}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <Stack gap={2} className="mt-2">
                      {sectionBranches.map(branch =>
                        branch.derogations?.some(derogation => derogation.status === 'CREATED') ? (
                          lineItem(branch)
                        ) : (
                          <ArchivedEventsLineItem
                            key={branch.id}
                            branch={branch}
                            alreadyRestored={restoredBranchIds.has(branch.id)}
                            refetchPolicyOnDashboard={refetchPolicyOnDashboard}
                          />
                        ),
                      )}
                    </Stack>
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                <Stack gap={2}>
                  <div className="font-semibold">{sectionLabel(section)}</div>
                  {sectionBranches.map(lineItem)}
                </Stack>
              )}
            </Fragment>
          ))}
        </CardContent>
      </Stack>
    </Card>
  );
}

/** `stopFunnelBasePath`: where the app routes the stop funnel; without it a draft can't be resumed. */
export function PolicyDashboardEventsModule({
  stopFunnelBasePath,
}: {
  stopFunnelBasePath?: string;
} = {}) {
  const { branches, policy, mutate: refetchPolicyOnDashboard } = usePolicyOnDashboard();

  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: firmType } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user?.brokerageFirmId!),
    select: (data: { firmType: string }) => data.firmType,
    enabled: !!user?.brokerageFirmId,
  });
  const isWholesale = firmType === 'WHOLESALE';

  return (
    <Stack>
      {CARD_BRANCH_TYPES.map(branchType => (
        <EventsCardModule
          key={branchType}
          branchType={branchType}
          branches={branches}
          isWholesale={isWholesale}
          policyId={policy.id}
          refetchPolicyOnDashboard={refetchPolicyOnDashboard}
          stopFunnelBasePath={stopFunnelBasePath}
        />
      ))}
    </Stack>
  );
}
